import { Badge, Button, Input } from '@salesforce/design-system-react';
import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition, SpecState } from '../qualityFormulaTypes';
import type { QualityFormulaStore } from '../store/qualityFormulaStore';

import { toNumberOrDefault } from '../numberInputValue';
import {
  CONTRACT_PRICE_COMPONENTS,
  CONTRACT_UNIT_OF_MEASURE,
  getSpecUnit,
  resolveTestPriceComponentsTotal,
} from '../qualityFormulaMetadata';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { AnimatedItem } from './AnimatedItem';
import { getDeemedPriceIfApplicable, getSelectedPriceComponentKeysJoined } from './utils/formulaTestingPanelSelectors';
import { formatCurrency } from './utils/resultFormatting';

interface FormulaTestingPanelProps {
  compact?: boolean;
  description: string;
  enabledSpecs: SpecDefinition[];
  // Contract Entry's "Reset" button sits next to the Test button; the Template Editor has no
  // equivalent, so this is the only slot left open rather than baking Reset into this component.
  extraActions?: React.ReactNode;
  priceLabel?: string;
  quantityLabel?: string;
  /** Hides this panel's own title/description when the Playground Card supplies its header. */
  showHeading?: boolean;
  testButtonLabel: string;
  title: string;
}

interface SpecPriceComponentsInputsProps {
  components: typeof CONTRACT_PRICE_COMPONENTS;
  onChange: (key: string, value: number) => void;
  specLabel: string;
  /** Applied by `AnimatedItem` to fade this spec's block in when it's newly added — see its
   * own doc comment. */
  style?: React.CSSProperties;
  values: Record<string, number>;
}

// One spec's badge + its selected components' editable inputs — split out purely so the spec tag
// and its inputs read as one reusable unit instead of being inlined into FormulaTestingPanel's own
// render. This is the one place a spec badge appears in the whole panel (every other field below
// uses a plain descriptive label instead) — with several specs possibly scaling against Price
// Components at once, the badge is the only thing telling the editable fields underneath it apart.
const SpecPriceComponentsInputs: React.FC<SpecPriceComponentsInputsProps> = ({
  components,
  onChange,
  specLabel,
  style,
  values,
}) => {
  const testBasis = resolveTestPriceComponentsTotal(
    components.map(component => component.key),
    values,
  );

  return (
    <div className="slds-box slds-theme_default" style={{ ...style, width: 'fit-content' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Badge color="light" content={specLabel} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {components.map(component => (
            <Input
              key={component.key}
              label={component.label}
              onChange={(_event, data) => {
                onChange(component.key, toNumberOrDefault(data.value, 0));
              }}
              styleContainer={{ width: 110 }}
              type="number"
              value={values[component.key]}
            />
          ))}
        </div>
        <p className="slds-text-body_small" style={{ fontWeight: 600, margin: 0 }}>
          Test Basis: {formatCurrency(testBasis)} / {CONTRACT_UNIT_OF_MEASURE}
        </p>
        <p className="slds-text-body_small slds-text-color_weak" style={{ margin: 0 }}>
          Only selected components are editable.
        </p>
      </div>
    </div>
  );
};

interface ActualValueInputProps {
  label: string;
  onChange: (value: number) => void;
  style?: React.CSSProperties;
  value: number;
}

// Mirrors FormulaField's own draft-state pattern (see its doc comment): the input's own text is
// tracked separately from the numeric value it reports upward, so the field can sit empty
// mid-edit instead of being forced back to a number (and therefore back to 0) on every keystroke.
// A blank or otherwise non-numeric draft snaps back to 0 on blur.
const ActualValueInput: React.FC<ActualValueInputProps> = ({ label, onChange, style, value }) => {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
    // Resync only on external `value` changes, not local edits.
  }, [value]);

  return (
    <Input
      label={label}
      onBlur={() => {
        if (draft === '' || Number.isNaN(Number(draft))) {
          setDraft('0');
          onChange(0);
        }
      }}
      onChange={(_event, data) => {
        setDraft(data.value);
        if (data.value !== '' && !Number.isNaN(Number(data.value))) {
          onChange(Number(data.value));
        }
      }}
      placeholder="e.g. 9.25"
      styleContainer={style}
      type="number"
      value={draft}
    />
  );
};

/**
 * Builds a flat, primitive-only, spec-keyed record by applying `pick` to each of `enabledSpecs`'
 * live `SpecState` — the shared shape behind both price-basis derivations in `FormulaTestingPanel`
 * (Price Components keys, Deemed Price), so the same `useShallow`-safe reduce only lives once.
 * `useShallow` only bails out of a re-render when the selector's own output is shallowly equal to
 * last time, which only holds for primitives — `pick` must return a primitive (or `undefined` when
 * it doesn't apply to a given spec; see `getSelectedPriceComponentKeysJoined`/
 * `getDeemedPriceIfApplicable`), never a freshly-built array/object.
 */
const useSpecStatePrimitiveBySpec = (
  enabledSpecs: SpecDefinition[],
  pick: (specState: SpecState) => string | undefined,
): Record<string, string | undefined> =>
  useQualityFormulaStore(
    useShallow((store: QualityFormulaStore) =>
      enabledSpecs.reduce<Record<string, string | undefined>>((acc, spec) => {
        acc[spec.key] = pick(store.specStates[spec.key]);
        return acc;
      }, {}),
    ),
  );

/**
 * The "enter sample values, run the calculate API, review the button" section shared by Contract
 * Entry and the Template Manager's Formula Testing panel — both need the exact same
 * quantity/price/actual-value inputs wired to the same quality-formula store's preview call, so
 * this is the single place that skeleton lives instead of being copy-pasted per page. Reads its
 * own test-value/preview state straight from the store (rather than taking it as props) since
 * both call sites were only ever passing their own `useQualityFormulaStore` selectors straight
 * back down — only genuinely page-specific config (labels, copy, the Reset slot) remains a prop.
 */
export const FormulaTestingPanel: React.FC<FormulaTestingPanelProps> = ({
  compact = false,
  description,
  enabledSpecs,
  extraActions,
  priceLabel = 'Contract Price',
  quantityLabel = 'Delivered Quantity',
  showHeading = true,
  testButtonLabel,
  title,
}) => {
  const {
    actualValues,
    deliveredQuantity,
    isTesting,
    runPreview,
    setActualValues,
    setDeliveredQuantity,
    setTestComponentValue,
    setTestDeemedPriceOverride,
    setTradePrice,
    testComponentValues,
    testDeemedPriceOverrides,
    tradePrice,
  } = useQualityFormulaStore(
    useShallow(store => ({
      actualValues: store.actualValues,
      deliveredQuantity: store.deliveredQuantity,
      isTesting: store.isTesting,
      runPreview: store.runPreview,
      setActualValues: store.setActualValues,
      setDeliveredQuantity: store.setDeliveredQuantity,
      setTestComponentValue: store.setTestComponentValue,
      setTestDeemedPriceOverride: store.setTestDeemedPriceOverride,
      setTradePrice: store.setTradePrice,
      testComponentValues: store.testComponentValues,
      testDeemedPriceOverrides: store.testDeemedPriceOverrides,
      tradePrice: store.tradePrice,
    })),
  );

  const selectedKeysBySpec = useSpecStatePrimitiveBySpec(enabledSpecs, getSelectedPriceComponentKeysJoined);

  // Per-spec components shown in the Price Components section below, for specs scaling against
  // Price Components instead of the shared Contract Price — every enabled spec gets an entry
  // (possibly empty) so indexing below never needs an undefined check.
  const priceComponentsBySpec = useMemo(
    () =>
      Object.entries(selectedKeysBySpec).reduce<Record<string, typeof CONTRACT_PRICE_COMPONENTS>>(
        (acc, [specKey, keys]) => {
          const selectedKeys = keys ? keys.split(',') : [];
          acc[specKey] = CONTRACT_PRICE_COMPONENTS.filter(component => selectedKeys.includes(component.key));
          return acc;
        },
        {},
      ),
    [selectedKeysBySpec],
  );
  const specsWithPriceComponents = enabledSpecs.filter(spec => priceComponentsBySpec[spec.key].length > 0);

  // Only ever used as the displayed input's fallback (via `testDeemedPriceOverrides`) — testing a
  // formula must never change what's actually configured for a spec, only read it as a starting
  // point (see store's own doc comment).
  const deemedPriceBySpec = useSpecStatePrimitiveBySpec(enabledSpecs, getDeemedPriceIfApplicable);
  const specsWithDeemedPrice = enabledSpecs.filter(spec => deemedPriceBySpec[spec.key] !== undefined);

  return (
    <section
      aria-label="Test formulas with sample values"
      // Both the workbench and template editor use the same Playground wrapper and spacing.
      className={`qfw-formula-testing${compact ? ' qfw-formula-testing-compact' : ''}`}
      style={{ display: 'flex', flexDirection: 'column', gap: compact ? 'var(--slds-g-spacing-2)' : 16 }}
    >
      {showHeading ? (
        <div>
          <h2 className="slds-text-heading_small">{title}</h2>
          <p className="slds-text-body_regular slds-text-color_weak" style={{ maxWidth: 640 }}>
            {description}
          </p>
        </div>
      ) : null}

      <div className="qfw-test-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span className="qfw-playground-section-label slds-text-title_caps slds-text-color_weak">Shared Inputs</span>
        <div className="qfw-playground-grid">
          <Input
            label={quantityLabel}
            onChange={(_event, data) => {
              setDeliveredQuantity(toNumberOrDefault(data.value, 0));
            }}
            type="number"
            value={deliveredQuantity}
          />
          <Input
            label={priceLabel}
            onChange={(_event, data) => {
              setTradePrice(toNumberOrDefault(data.value, 0));
            }}
            type="number"
            value={tradePrice}
          />
        </div>
        {specsWithDeemedPrice.map(spec => (
          <AnimatedItem key={spec.key} visible>
            {styles => (
              <div style={{ ...styles, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Input
                  label={`${spec.label} Deemed Price ($ / ${CONTRACT_UNIT_OF_MEASURE})`}
                  onChange={(_event, data) => {
                    setTestDeemedPriceOverride(spec.key, toNumberOrDefault(data.value, 0));
                  }}
                  styleContainer={{ maxWidth: 260 }}
                  type="number"
                  value={testDeemedPriceOverrides[spec.key] ?? Number(deemedPriceBySpec[spec.key])}
                />
                <p className="slds-text-body_small slds-text-color_weak" style={{ margin: 0 }}>
                  Test override for {spec.label}; configured terms stay unchanged.
                </p>
              </div>
            )}
          </AnimatedItem>
        ))}
      </div>

      {specsWithPriceComponents.length > 0 ? (
        <div className="qfw-test-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="qfw-playground-section-label slds-text-title_caps slds-text-color_weak">
            Price Components
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {specsWithPriceComponents.map(spec => (
              <AnimatedItem key={spec.key} visible>
                {styles => (
                  <SpecPriceComponentsInputs
                    components={priceComponentsBySpec[spec.key]}
                    onChange={setTestComponentValue}
                    specLabel={spec.label}
                    style={styles}
                    values={testComponentValues}
                  />
                )}
              </AnimatedItem>
            ))}
          </div>
        </div>
      ) : null}

      <div className="qfw-test-group qfw-test-specs" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span className="qfw-playground-section-label slds-text-title_caps slds-text-color_weak">
          Actual Value{enabledSpecs.length === 1 ? '' : 's'}
        </span>
        <div className="qfw-playground-grid">
          {enabledSpecs.map(spec => {
            const unit = getSpecUnit(spec.key);
            return (
              <AnimatedItem key={spec.key} visible>
                {styles => (
                  <ActualValueInput
                    label={unit === '%' ? `${spec.label} (%)` : spec.label}
                    onChange={value => {
                      setActualValues(current => ({ ...current, [spec.key]: value }));
                    }}
                    style={styles}
                    value={actualValues[spec.key]}
                  />
                )}
              </AnimatedItem>
            );
          })}
        </div>
      </div>

      <div className="qfw-test-actions" style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
        <Button
          disabled={isTesting}
          label={isTesting ? 'Testing…' : testButtonLabel}
          onClick={() => {
            void runPreview();
          }}
          variant="brand"
        />
        {extraActions}
      </div>
    </section>
  );
};
